import { FunctionComponent, useContext, useState, type JSX } from 'react';
import { PaddingProps, SpaceProps } from 'styled-system';
import styled, { css, ThemeContext } from 'styled-components';
import { Group } from '../containers/Group/Group.component';
import { Card } from '../Card/Card.component';
import { Icon } from '../icons/Icon.component';
import { Text } from '../typography';
import { Flex } from '../containers/Flex/Flex.component';

export interface IInlineNotificationProps extends SpaceProps, PaddingProps {
  variant: 'success' | 'warning' | 'danger' | 'announcement' | 'info';
  title?: string | JSX.Element;
  noIcon?: boolean;
  accent?: boolean;
  children?: any;
  subject?: string;
  bold?: boolean;
  noClose?: boolean;
  onClose?: () => any;
  sharpCorners?: boolean;
  fullWidth?: boolean;
  shouldCenterIcon?: boolean;
  shouldCenterCloseIcon?: boolean;
  icon?:
    | 'circle-check-solid'
    | 'triangle-exclamation-solid'
    | 'diamond-exclamation-solid'
    | 'bullhorn-solid'
    | 'circle-info-solid';
  padding?: number;
}

const StyledIcon = styled(Icon)<{ shouldCenterCloseIcon?: boolean }>`
  margin-left: auto;
  margin-top: ${({ shouldCenterCloseIcon }) => (shouldCenterCloseIcon ? 'auto' : '0')};
  margin-bottom: ${({ shouldCenterCloseIcon }) => (shouldCenterCloseIcon ? 'auto' : '0')};
  padding: 2px 0 2px 2px;
  :hover {
    cursor: pointer;
  }
`;

const StyledCard = styled(Card)<{ sharpCorners?: boolean }>`
  ${({ sharpCorners }) =>
    sharpCorners &&
    css`
      border-radius: 0;
    `}
`;

export const InlineNotification: FunctionComponent<IInlineNotificationProps> = ({
  title,
  noIcon = false,
  children,
  variant,
  subject,
  onClose,
  bold = false,
  noClose = false,
  accent = false,
  fullWidth = false,
  shouldCenterIcon = true,
  shouldCenterCloseIcon = false,
  icon = null,
  padding = 4,
  ...props
}) => {
  const [isOpenInternal, setIsOpenInternal] = useState(true);

  const {
    inlineNotification: { variants },
  } = useContext(ThemeContext);

  const currentVariant = bold ? variants[variant].bold : variants[variant].light;

  const close = () => {
    if (onClose) {
      onClose();
    }

    setIsOpenInternal(false);
  };

  if (!isOpenInternal) return null;

  return (
    <StyledCard
      borderless
      accentLocation={'left'}
      backgroundColor={currentVariant.backgroundColor}
      accentColor={accent ? currentVariant.accentColor : undefined}
      p={padding}
      pr={5}
      context={`inline-notification-${variant}`}
      subject={subject}
      width={fullWidth ? '100%' : null}
      {...props}
    >
      <Group justifyContent="space-between" fullWidth>
        <Group
          justifyContent={fullWidth ? 'center' : 'flex-start'}
          alignItems={(!children || !title) && shouldCenterIcon ? 'center' : 'flex-start'}
          fullWidth
        >
          {!noIcon && (
            <Flex justifyContent="center" alignItems="center">
              <Icon color={currentVariant.iconColor} icon={icon || currentVariant.icon} size={16} />
            </Flex>
          )}
          <Group vertical fullWidth={!fullWidth}>
            {typeof title === 'string' ? (
              <Text weight={700} color={currentVariant.textColor}>
                {title}
              </Text>
            ) : (
              title
            )}
            {children && (
              <Text weight={400} color={currentVariant.textColor}>
                {children}
              </Text>
            )}
          </Group>
        </Group>
        {!noClose && (
          <StyledIcon
            color={currentVariant.textColor}
            icon={'xmark-regular'}
            size={14}
            onClick={close}
            shouldCenterCloseIcon={shouldCenterCloseIcon}
          />
        )}
      </Group>
    </StyledCard>
  );
};

InlineNotification.displayName = 'InlineNotification';
